import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Landmark, FileText, ShieldCheck, Leaf, Scale, Ban, Wrench, AlertTriangle, BadgeCheck, Radio, Gauge, ArrowRight, Fingerprint } from 'lucide-react';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, p) => a + (b - a) * p;
const easeInOut = (p) => (p < 0.5 ? 2 * p * p : 1 - ((-2 * p + 2) ** 2) / 2);
const nf = (n) => Math.round(n).toLocaleString('en-IN');
const GLYPHS = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#%&/';

const K = {
  base: '#050914', surface: '#0F172A', elev: '#1E293B',
  sec: '#94A3B8', mut: '#64748B',
  em: '#10B981', am: '#F59E0B', red: '#EF4444', redS: '#450A0A', redB: '#991B1B', scan: '#22D3EE',
};
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const useScramble = (target, run) => {
  const [out, setOut] = useState('');
  const raf = useRef();
  useEffect(() => {
    if (!run) { setOut(''); return undefined; }
    const start = performance.now();
    const dur = 520;
    const tick = (now) => {
      const p = clamp((now - start) / dur, 0, 1);
      const settled = Math.floor(p * target.length);
      let s = '';
      for (let i = 0; i < target.length; i += 1) {
        const ch = target[i];
        if (i < settled || ch === ' ' || ch === '·' || ch === '#') s += ch;
        else s += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      }
      setOut(s);
      if (p < 1) raf.current = requestAnimationFrame(tick); else setOut(target);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [run, target]);
  return run ? out : '';
};

const Corner = ({ c }) => (
  <>
    {['top-0 left-0 border-t border-l', 'top-0 right-0 border-t border-r', 'bottom-0 left-0 border-b border-l', 'bottom-0 right-0 border-b border-r'].map((p) => (
      <span key={p} className={`pointer-events-none absolute ${p} h-2 w-2`} style={{ borderColor: c }} />
    ))}
  </>
);

const CLEAN = [
  { id: 'rto', src: 'VAHAN', Icon: FileText, title: 'RTO Registration', short: 'RTO', code: 'RTO', value: 'KA 01 · Bengaluru', status: 'REGISTERED', reveal: 0.12 },
  { id: 'insurance', src: 'IIB', Icon: ShieldCheck, title: 'Insurance', short: 'Insurance', code: 'INS', value: 'Comprehensive · Mar 2027', status: 'ACTIVE', reveal: 0.18 },
  { id: 'puc', src: 'PUCC', Icon: Leaf, title: 'Emission · PUC', short: 'PUC', code: 'PUC', value: 'Within norms', status: 'COMPLIANT', reveal: 0.24 },
  { id: 'crime', src: 'NCRB', Icon: Scale, title: 'Crime & Legal', short: 'Legal', code: 'LAW', value: 'No FIR · no challans', status: 'CLEAR', reveal: 0.30 },
  { id: 'blacklist', src: 'VAHAN', Icon: Ban, title: 'Blacklist / Hypoth.', short: 'Blacklist', code: 'B/L', value: 'No lien on title', status: 'CLEAR', reveal: 0.36 },
];

const CleanCard = ({ r, t, compact = false }) => {
  const on = t >= r.reveal;
  const started = t >= r.reveal - 0.06;
  const col = on ? K.em : (started ? K.am : K.mut);
  const { Icon } = r;
  if (compact) {
    return (
      <div data-testid={`record-${r.id}-m`} className="relative flex flex-col items-center gap-0.5 overflow-hidden rounded-sm py-1.5 transition-colors duration-300" style={{ background: K.surface, border: `1px solid ${on ? 'rgba(16,185,129,0.5)' : 'rgba(51,65,85,0.9)'}` }}>
        <Icon className="h-3.5 w-3.5" style={{ color: col }} />
        <span className="font-mono text-[8px] font-black uppercase tracking-wider text-white">{r.code}</span>
        {on ? <BadgeCheck className="h-2.5 w-2.5" style={{ color: K.em }} /> : <span className={`h-1.5 w-1.5 rounded-full ${started ? 'animate-pulse' : ''}`} style={{ background: col }} />}
      </div>
    );
  }
  return (
    <motion.div data-testid={`record-${r.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="relative flex flex-col justify-between overflow-hidden rounded-md p-2.5 transition-colors duration-300" style={{ background: K.surface, border: `1px solid ${on ? 'rgba(16,185,129,0.55)' : 'rgba(51,65,85,0.9)'}`, boxShadow: on ? `inset 0 0 20px rgba(16,185,129,0.08)` : 'none' }}>
      <Corner c={on ? 'rgba(16,185,129,0.7)' : 'rgba(100,116,139,0.5)'} />
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-[0.16em]" style={{ color: col }}>
          <span className={`h-1.5 w-1.5 rounded-full ${on ? '' : 'animate-pulse'}`} style={{ background: col }} />{r.src}
        </span>
        <Icon className="h-3.5 w-3.5" style={{ color: on ? K.em : K.mut }} />
      </div>
      <div className="mt-2">
        <div className="truncate text-[10px] font-medium uppercase tracking-[0.1em]" style={{ color: K.sec }}>{r.short}</div>
        <div className="mt-0.5 inline-flex items-center gap-1 font-mono text-xs font-black tracking-wide" style={{ color: on ? K.em : K.mut }}>
          {on ? <><BadgeCheck className="h-3 w-3" /> {r.status}</> : (started ? 'VERIFYING…' : 'QUEUED')}
        </div>
      </div>
    </motion.div>
  );
};

const RcHero = ({ t, compact = false, flagged }) => {
  const reg = useScramble('KA 01 MJ 4291', t >= 0.06);
  const display = t >= 0.06 ? (reg || 'KA 01 MJ 4291') : '## ## ## ####';
  const online = t >= 0.1;
  const cleanCount = CLEAN.filter((r) => t >= r.reveal).length;
  return (
    <div data-testid={`rc-hero-card${compact ? '-m' : ''}`} className="relative w-full overflow-hidden rounded-md" style={{ background: K.surface, border: `1px solid ${flagged ? 'rgba(153,27,27,0.6)' : 'rgba(30,41,59,1)'}`, boxShadow: flagged ? `0 0 30px -10px ${K.red}` : '0 0 30px -14px rgba(16,185,129,0.3)' }}>
      <Corner c={flagged ? K.redB : 'rgba(16,185,129,0.5)'} />
      {/* tracing beam while scanning */}
      {t < 0.5 && <div className="pointer-events-none absolute inset-x-0" style={{ top: `${(clamp((t - 0.06) / 0.3, 0, 1)) * 100}%`, height: 2, background: `linear-gradient(90deg,transparent,${K.scan},transparent)`, boxShadow: `0 0 14px 1px ${K.scan}`, opacity: 0.8 }} />}
      <div className={`relative ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-emerald-300"><Landmark className="h-3.5 w-3.5" /><span className="text-[8px] font-bold uppercase tracking-[0.2em]">Registration Certificate</span></span>
          <span className="rounded-sm border border-white/15 bg-white/5 px-1.5 py-0.5 text-[8px] font-black tracking-widest text-white">IND</span>
        </div>
        <div className="mt-2.5 text-[8px] font-mono uppercase tracking-[0.2em]" style={{ color: K.mut }}>Registration No.</div>
        <div className="font-mono font-black leading-none tracking-tight text-white" style={{ fontSize: compact ? '1.5rem' : '2.4rem', textShadow: `0 0 24px ${K.scan}44` }}>{display}</div>
        <div className="mt-1.5 font-mono text-[10px] sm:text-xs" style={{ color: K.sec }}>Hyundai Creta SX(O) · 2022 · Petrol</div>
        {!compact && (
          <div className="mt-3 flex items-end gap-[2px] h-5 opacity-80">
            {Array.from({ length: 46 }).map((_, i) => (<span key={i} className="flex-1" style={{ height: `${40 + ((i * 37) % 60)}%`, background: i % 4 === 0 ? 'rgba(16,185,129,0.85)' : 'rgba(148,163,184,0.3)' }} />))}
          </div>
        )}
        {/* uplink status */}
        <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: online ? K.em : K.scan }}>
            <Radio className={`h-3 w-3 ${online ? '' : 'animate-pulse'}`} /> {online ? 'VAHAN · Online' : 'Linking…'}
          </span>
          <span className="font-mono text-[9px] tabular-nums" style={{ color: flagged ? K.red : K.em }}>{cleanCount}/5 clean{flagged ? ' · 1 flag' : ''}</span>
        </div>
      </div>
    </div>
  );
};

const ServiceAlert = ({ t, compact = false }) => {
  const scanning = t >= 0.4 && t < 0.52;
  const red = t >= 0.52;
  const punch = red ? lerp(0.97, 1, clamp((t - 0.52) / 0.06, 0, 1)) : 1;
  const accidentOn = t >= 0.6;
  const accFull = 'FRONT-END STRUCTURAL REPAIR · WORKSHOP LOG 2023';
  const accShort = 'STRUCTURAL REPAIR · 2023';
  const accidentTxt = useScramble(compact ? accShort : accFull, accidentOn);
  const odoP = easeInOut(clamp((t - 0.62) / 0.16, 0, 1));
  const odo = lerp(42000, 61200, odoP);
  const odoShown = t >= 0.62;
  const border = red ? K.redB : (scanning ? 'rgba(245,158,11,0.5)' : 'rgba(51,65,85,0.9)');
  const bg = red ? K.redS : K.surface;
  return (
    <div data-testid={`record-service-alert${compact ? '-m' : ''}`} className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-md transition-colors duration-200" style={{ background: bg, border: `2px solid ${border}`, boxShadow: red ? `0 0 44px -8px ${K.red}, inset 0 0 40px rgba(239,68,68,0.12)` : 'none', transform: `scale(${punch})` }}>
      <Corner c={red ? K.red : (scanning ? K.am : 'rgba(100,116,139,0.5)')} />
      {/* header */}
      <div className={`relative flex shrink-0 items-center justify-between ${compact ? 'px-3 pt-2.5' : 'px-4 pt-3'}`}>
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[10px] sm:text-xs font-black uppercase tracking-[0.14em] text-white">
          <Wrench className="h-4 w-4 shrink-0" style={{ color: red ? K.red : K.sec }} /> Service History{!compact && <span style={{ color: K.mut }} className="text-[8px]"> · SRC OEM</span>}
        </span>
        {red ? (
          <span className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-white animate-pulse" style={{ background: K.red, boxShadow: `0 0 16px ${K.red}` }}>
            <AlertTriangle className="h-3.5 w-3.5" /> Critical Finding
          </span>
        ) : (
          <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: scanning ? K.am : K.mut }}>{scanning ? 'Deep scanning…' : 'Queued'}</span>
        )}
      </div>

      {/* scanning shimmer bar (pivot/pause) */}
      {!red && (
        <div className={`relative flex flex-1 flex-col justify-center ${compact ? 'px-3' : 'px-4'}`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: K.mut }}>{scanning ? 'Cross-checking workshop & OEM logs…' : 'Awaiting deep scan of workshop records'}</div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full" style={{ background: 'rgba(148,163,184,0.15)' }}>
            <div className="h-full w-1/3 rounded-full" style={{ background: K.am, boxShadow: `0 0 8px ${K.am}`, transform: `translateX(${(scanning ? (Math.sin(t * 60) * 0.5 + 0.5) : 0) * 200}%)` }} />
          </div>
        </div>
      )}

      {/* the two findings */}
      {red && (
        <div className={`relative grid min-h-0 flex-1 ${compact ? 'grid-rows-2 gap-2 p-3 pt-2' : 'grid-cols-2 gap-3 p-4 pt-3'}`}>
          {/* accident */}
          <div className={`relative flex flex-col overflow-hidden rounded-sm ${compact ? 'justify-center p-2' : 'justify-center gap-3 p-3'}`} style={{ background: 'rgba(0,0,0,0.35)', border: `1px solid ${K.redB}` }}>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[8px] sm:text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: K.red }}><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Accident repair logged</div>
              <div className={`mt-1 font-mono font-bold leading-snug text-white ${compact ? 'truncate text-[11px]' : 'text-sm'}`}>{accidentOn ? (accidentTxt || (compact ? accShort : accFull)) : '·······················'}</div>
              {!compact && <div className="mt-1 text-[9px]" style={{ color: K.sec }}>Not disclosed by seller</div>}
            </div>
            {!compact && accidentOn && (
              <div className="mt-3 grid grid-cols-2 gap-1.5 font-mono text-[9px]">
                {[['Impact zone', 'FRONT-LEFT'], ['Severity', 'STRUCTURAL'], ['Airbags', 'NOT DEPLOYED'], ['Panels repainted', '03']].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between rounded-sm px-2 py-1" style={{ background: 'rgba(153,27,27,0.18)', border: '1px solid rgba(153,27,27,0.4)' }}>
                    <span style={{ color: K.sec }} className="uppercase tracking-wider">{k}</span>
                    <span className="font-black text-white">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* odometer rollback */}
          <div className={`relative flex flex-col overflow-hidden rounded-sm ${compact ? 'justify-center p-2' : 'justify-center gap-3 p-3'}`} style={{ background: 'rgba(0,0,0,0.35)', border: `1px solid ${K.redB}` }}>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[8px] sm:text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: K.red }}><Gauge className="h-3.5 w-3.5 shrink-0" /> Odometer rollback</div>
              <div data-testid={`odometer-mismatch-value${compact ? '-m' : ''}`} className="mt-1 flex items-center gap-1.5 font-mono font-black">
                <span className={`line-through ${compact ? 'text-xs' : 'text-sm'}`} style={{ color: K.mut }}>42,000</span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0" style={{ color: K.red }} />
                <span className={compact ? 'text-base' : 'text-2xl'} style={{ color: K.red, textShadow: `0 0 16px ${K.red}88` }}>{odoShown ? nf(odo) : '42,000'}</span>
                <span className="text-[10px]" style={{ color: K.sec }}>km</span>
              </div>
              {!compact && <div className="mt-1 text-[9px]" style={{ color: K.sec }}>Dash reading vs service logs · Δ +19,200 km</div>}
            </div>
            {!compact && (
              <div className="mt-3 space-y-2 font-mono text-[9px]">
                <div>
                  <div className="mb-0.5 flex items-center justify-between"><span style={{ color: K.sec }} className="uppercase tracking-wider">Dash claims</span><span className="text-slate-400">42,000 km</span></div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: 'rgba(148,163,184,0.15)' }}><div className="h-full rounded-full" style={{ width: '68.6%', background: K.mut }} /></div>
                </div>
                <div>
                  <div className="mb-0.5 flex items-center justify-between"><span style={{ color: K.red }} className="uppercase tracking-wider font-bold">Verified logs</span><span className="font-black" style={{ color: K.red }}>{odoShown ? nf(odo) : '42,000'} km</span></div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: 'rgba(148,163,184,0.15)' }}><div className="h-full rounded-full" style={{ width: `${lerp(68.6, 100, odoP)}%`, background: K.red, boxShadow: `0 0 8px ${K.red}` }} /></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const VerdictBanner = ({ t, mobile = false }) => {
  const p = clamp((t - 0.84) / 0.06, 0, 1);
  return (
    <div className="shrink-0 overflow-hidden" style={{ maxHeight: `${lerp(0, 64, p)}px` }}>
      <div data-testid={`verdict-banner${mobile ? '-m' : ''}`} className="mt-3 flex items-center justify-center gap-2 sm:gap-3 rounded-sm px-3 sm:px-5 py-2.5 sm:py-3" style={{ background: K.red, boxShadow: `0 0 40px -6px ${K.red}`, opacity: p }}>
        <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 text-white" />
        <span className="font-mono text-[11px] sm:text-lg font-black uppercase tracking-[0.12em] sm:tracking-[0.18em] text-white text-center leading-tight">Records Alert · Odometer Rollback + Undisclosed Accident</span>
      </div>
    </div>
  );
};

const RecordsBeat = ({ t = 0 }) => {
  const online = t >= 0.1;
  const flagged = t >= 0.52;
  const redFlash = clamp((t - 0.52) / 0.02, 0, 1) * (1 - clamp((t - 0.56) / 0.05, 0, 1));
  const redAmbient = flagged ? 0.1 : 0;
  return (
    <motion.div data-testid="records-beat" className="absolute inset-0 z-40 overflow-hidden" style={{ background: K.base }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
      {/* engineering grid */}
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(148,163,184,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,0.05) 1px,transparent 1px)', backgroundSize: '42px 42px' }} />
      {/* red ambient + flash on reveal */}
      <div className="pointer-events-none absolute inset-0 transition-opacity" style={{ background: `radial-gradient(80% 70% at 62% 72%, ${K.red}, transparent 70%)`, opacity: redAmbient }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: K.red, opacity: redFlash * 0.28 }} />
      {/* crt + vignette + grain */}
      <div className="pointer-events-none absolute inset-0 z-10 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '100% 4px' }} />
      <div className="pointer-events-none absolute inset-0 z-10" style={{ background: 'radial-gradient(120% 92% at 50% 42%, rgba(0,0,0,0) 48%, rgba(5,9,20,0.75) 100%)' }} />
      <div className="pointer-events-none absolute z-10 mix-blend-overlay opacity-[0.05] animate-[grainShift_1.1s_steps(3)_infinite]" style={{ inset: '8px', backgroundImage: GRAIN, backgroundSize: '160px 160px' }} />

      <div className="relative z-20 flex h-full w-full flex-col p-3 sm:p-6">
        {/* header */}
        <div className="flex shrink-0 items-center justify-between gap-2">
          <div className="inline-flex min-w-0 items-center gap-2">
            <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border border-emerald-500/40 px-2 py-1 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.14em] sm:tracking-[0.18em] text-emerald-300" style={{ background: K.surface }}><Fingerprint className="h-3.5 w-3.5" /> Sentinel Terminal</span>
            <span className="hidden md:inline whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: K.mut }}>Govt Records Verification</span>
          </div>
          <div className="inline-flex shrink-0 items-center gap-2">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[9px] sm:text-[10px] uppercase tracking-wider" style={{ color: flagged ? K.red : (online ? K.em : K.scan) }}>
              <span className={`h-1.5 w-1.5 rounded-full ${online && !flagged ? '' : 'animate-pulse'}`} style={{ background: flagged ? K.red : (online ? K.em : K.scan), boxShadow: `0 0 6px ${flagged ? K.red : K.em}` }} />
              <span className="sm:hidden">{flagged ? 'Flag' : 'Sync'}</span><span className="hidden sm:inline">{flagged ? 'Finding detected' : (online ? 'Sync 100%' : 'Linking…')}</span>
            </span>
          </div>
        </div>

        {/* desktop */}
        <div className="hidden sm:flex flex-1 min-h-0 mt-4 flex-col">
          <div className="grid flex-1 min-h-0 grid-cols-12 gap-4">
            <div className="col-span-4 flex min-h-0 flex-col">
              <RcHero t={t} flagged={flagged} />
            </div>
            <div className="col-span-8 flex min-h-0 flex-col gap-4">
              <div className="grid shrink-0 grid-cols-5 gap-3">
                {CLEAN.map((r) => <CleanCard key={r.id} r={r} t={t} />)}
              </div>
              <ServiceAlert t={t} />
            </div>
          </div>
          <VerdictBanner t={t} />
        </div>

        {/* mobile */}
        <div className="sm:hidden flex flex-1 min-h-0 mt-2.5 flex-col gap-2">
          <RcHero t={t} compact flagged={flagged} />
          <div className="grid shrink-0 grid-cols-5 gap-1">
            {CLEAN.map((r) => <CleanCard key={r.id} r={r} t={t} compact />)}
          </div>
          <ServiceAlert t={t} compact />
          <VerdictBanner t={t} mobile />
        </div>
      </div>
    </motion.div>
  );
};

export default RecordsBeat;
